/**
 * THE VOLUMES — the single definition of every visual volume the product offers. Pure data (its one
 * import is the generated fx membership, JSON): catalogue.ts derives every picker, filter, gallery
 * lane, TV grid, the Director pools and Pro gating from it, and the web build emits it (resolved
 * against the pinned engine) as `dist/catalogue.json`, which the native phone shells read from their
 * bundled copy of the web build instead of keeping hand-written lists (the iOS/Android lists had
 * drifted: no source-code / source-subway). ⚠ Nothing else may list volume membership — add a world
 * HERE and every surface follows.
 *
 * Rules the rest of the code relies on:
 *  - A volume is OFFERED only when it is `listed` and at least one of its worlds resolves in the
 *    library right now (bundled, or registered by an OTA package).
 *  - The fx volumes (shader-pack visuals) are NOT hand lists: each declares a rule over the content
 *    CURATION (`fx`, data/fx-curation) and scripts/fx/build-fx-packs.mjs writes their members to
 *    fxMembership.json — every curated visual that passes quality, placed by genre. The curation's
 *    quality verdicts decide what is cut (near-white, needs-work); nothing here names a cut visual.
 *    What stays hand-made here is the product's own choice: the volumes, their order, the free picks.
 *  - A world belongs to at most ONE volume (asserted in volumes check below).
 *  - Membership and gating here are authoritative for every id listed here; an OTA package's own
 *    `volume`/`pro` metadata only places worlds this file does not name (see catalogue.ts).
 */
import FX_MEMBERSHIP from './fxMembership.json' with { type: 'json' }

export interface VolumeDef {
  /** Stable key — the gallery/TV filter value. 'v1'/'v2' are kept from the old GalleryFilter. */
  id: string
  /** Full name ("Volume 1") — gallery select, TV rail, paywall copy. */
  title: string
  /** Compact name ("Vol 1") — the visualizer's volume select and the TV tile tag. */
  short: string
  /** Tiny tile badge ("V1") — the native phone pickers. */
  badge: string
  /** One-line description, shown on the TV's locked-world panel ("Part of Volume 2 — …"). */
  blurb: string
  /** Ordered world ids. Order is the picker/grid order. */
  worlds: readonly string[]
  /** Pro volumes only: the free SHOWCASE — worlds every user keeps. */
  free: readonly string[]
  /** Pro (subscription) volume: every world except `free` needs SYQEL Art Pro. */
  pro: boolean
  /** Legacy OTA placement: packages whose signed metadata says `volume: n` join this volume. Only
   *  Volumes 1/2 have one — the SDK's wrapper generator bakes that number from Art's lists. */
  otaVolume?: number
  /** false = a HOLDING definition: its worlds are claimed (kept out of Experiments, the Director and
   *  every picker) but the volume is never offered. Default true. */
  listed?: boolean
  /** The phone feed's "For You" weight — how often its interleave reaches into this volume, relative
   *  to the others (default 1; 0 = never in For You — still in Browse). The feed leans on the style
   *  the product is heading for (tunnels, kaleidoscopes, ambient landscapes) without dropping the 3D
   *  scenes (owner, 1.6). Emitted in catalogue.json; the native feeds build their order from it. */
  feed?: number
  /** fx volumes only — WHICH shader-pack visuals this volume holds, as a rule over the curation
   *  (data/fx-curation/*.json, one entry per `<pack>/<slug>`): a visual joins the FIRST fx volume (in
   *  VOLUMES order) whose `genres` include its curated genre and, when `seasonal` is set, whose
   *  seasonal-ness matches (a `holiday`, or a theme in FX_SEASONAL_THEMES). `worlds` of such a volume is
   *  GENERATED (fxMembership.json, scripts/fx/build-fx-packs.mjs) — never list them by hand. */
  fx?: {
    genres: readonly string[]
    seasonal?: boolean
    /** How many of this volume's visuals ride INSIDE the app (the offline CORE set, apps/web/public/fx);
     *  the rest are EXTENDED — fetched from the fx asset base when online, hidden offline. */
    core: number
  }
  /** A TIER volume — offered only where its tier is. 'lite': the SDK's lite line (World.tier 'lite',
   *  geometry-first worlds that stay smooth at native resolution on the weakest GPUs) — offered on the weak TV
   *  tiers ONLY (owner, 2026-10-27: "our strongest customers are on stronger TVs and streamers"), and on a
   *  sender only for the lite worlds the TV it casts to offers (the offer frame's `extra`). Everywhere else its
   *  worlds are claimed (out of Experiments, the Director, every picker) and never listed (catalogue.ts). */
  tier?: 'lite'
  /** Packs whose visuals THIS volume already offers as engine shader worlds (id = the pack slug — the
   *  engine imported them as `shader` worlds). Their fx rebuilds are offered only where this volume does
   *  not list the slug, so no visual is offered twice (build-fx-packs). */
  fxTwinPacks?: readonly string[]
}

/** Curated themes that make a non-holiday `other` visual seasonal (the Seasons & Holidays rule). */
export const FX_SEASONAL_THEMES: readonly string[] = ['spring', 'summer', 'autumn', 'snow', 'winter-wonderland']

/** A volume's fx worlds as the generator last wrote them (empty until it has run once). */
const fxMembers = (id: string): readonly string[] => (FX_MEMBERSHIP.volumes as Record<string, readonly string[] | undefined>)[id] ?? []

/**
 * fx world ids are `<pack>/<slug>` — the shader-pack visual's own path (World mode 'fx', worldProtocol
 * 1.4), bundled as public/fx/<pack>/<slug>.json (fxWorlds.ts) and the id an OTA module
 * `world:<pack>/<slug>` would claim. The slash cannot collide with an Art id (all plain kebab-case);
 * wherever an id becomes a path it is a real directory level (public/fx/<pack>/, public/previews/<pack>/).
 * Curation reasoning: docs/VOLUMES_FX_CURATION.md.
 */
export const VOLUMES: readonly VolumeDef[] = [
  {
    // VOLUME 1 — the shipped stylized catalogue. earth-orbit + moonlit-lake (and source-code in
    // Volume 2) were retired by the owner (engine `retired`): dropped here so the data says it. They
    // still RESOLVE by id — a saved pick, a ?world= / home-channel deep link keeps mounting them.
    id: 'v1', title: 'Volume 1', short: 'Vol 1', badge: 'V1', blurb: 'the original stylized worlds',
    worlds: ['night-drive-3d', 'fireworks-bay', 'drone-show', 'snow-pass', 'star-voyage', 'starship', 'dune-sea', 'canyon-drive', 'caribbean-shore', 'verdant', 'river-run', 'magical-forest', 'abyss', 'coral-reef', 'gem-cave', 'golden-fields', 'lost-valley', 'volcano-isle', 'storm-front', 'neon-pulse', 'sacred-geometry', 'bass-citadel'],
    free: [], pro: false, otaVolume: 1, feed: 1
  },
  {
    // VOLUME 2 — the near-realistic remakes (one per V1 world, same journey concepts, the Midnight
    // Drive fidelity bar). Starfarer was the app's default/first visual until 1.4.0: returning free
    // users may have it pinned, so it stays free for everyone (and doubles as the taste of what Pro
    // buys).
    id: 'v2', title: 'Volume 2', short: 'Vol 2', badge: 'V2', blurb: 'the photoreal worlds',
    worlds: ['midnight-drive', 'alpine-pass', 'painted-canyon', 'elderwood', 'starfarer', 'tulip-stylized', 'digital-rain', 'inner-voyage', 'cosmic-eye', 'source-subway'],
    free: ['starfarer'], pro: true, otaVolume: 2, feed: 1
  },
  {
    // AMBIENT & CHILL — engine 0.14's "Wave 1": the 40 SYQEL Desktop free-ambient + free-chill pack
    // shaders, imported as `shader`-mode worlds (ids = the pack slugs). Ambient's 20 SCENES first,
    // grouped by what they show (sky and light → space and particles → warm light → water), then
    // Chill's 20 abstract fields from soft pastel to fully saturated. Free showcase: three calm,
    // distinct looks — a golden seascape, a cool aurora, a pastel mandala (one per colour family,
    // both halves of the pack). They join the Director's journey like every offered volume: all
    // 'mellow' and low-motion, so it reaches for them on quiet passages, and a weak TV offers them only
    // where it measured they render well (worldCost.ts). Owner decision (1.4.0): soft-fractal and
    // slow-kaleido MOVED to the fx volumes as their fx rebuilds (free-chill/soft-fractal in Tunnels,
    // free-chill/slow-kaleido in Kaleidoscope), so no visual is offered twice — 38 here; the two
    // shader ids are held below.
    id: 'ambient', title: 'Ambient & Chill', short: 'Ambient', badge: 'AC', blurb: 'slow light for calm listening',
    worlds: [
      'ocean-swell', 'horizon-haze', 'sand-dunes', 'misty-rays', 'aurora', 'nebula-drift', 'plasma-clouds', 'snowfall', 'dust-motes', 'candle-glow', 'slow-fire', 'lava-lamp', 'drifting-leaves', 'water-ripples', 'rain-on-glass', 'deep-caustics', 'bioluminescence', 'ink-in-water', 'silk-waves',
      'infinite-petals', 'pastel-storm', 'dream-smoke', 'cosmic-jelly', 'ember-silk', 'chroma-veil', 'spiral-drift', 'hypno-bloom', 'lucid-ripples', 'jelly-waves', 'liquid-chrome', 'glass-flow', 'ink-marble', 'melting-rainbow'
    ],
    free: ['ocean-swell', 'aurora', 'infinite-petals', 'water-ripples'], pro: true, feed: 3,
    // these ARE the two packs' visuals: their fx rebuilds join the fx volumes only for slugs not listed
    // above (soft-fractal / slow-kaleido moved; the rest of the packs' fx visuals the curation passes)
    fxTwinPacks: ['free-ambient', 'free-chill']
  },
  {
    // MOVED TO THE FX VOLUMES — held, never offered: the engine's shader versions of two Ambient &
    // Chill worlds whose fx rebuilds now ship in Tunnels / Kaleidoscope. Claimed here so the SDK's
    // copies stay out of Experiments and every picker; a saved pick or ?world= link still resolves
    // them, Pro-gated as they were.
    id: 'ambient-moved', title: 'Ambient & Chill (moved)', short: 'Ambient', badge: 'AC', blurb: 'slow light for calm listening',
    // + the near-white / pale scenes the owner pulled after seeing them on a phone (1.4.0)
    worlds: ['soft-fractal', 'slow-kaleido', 'halcyon', 'opal-drift', 'cloud-layers', 'color-tides', 'mirage'],
    free: [], pro: true, listed: false
  },
  {
    // LITE (shown as "Starter") — the SDK's lite line (worlds-three 0.9.0: 40 geometry-first worlds, every one measured smooth at
    // FULL detail and NATIVE resolution on the 1 GB EcoTV — 50–59 fps, p95 22–34 ms, 2026-10-27/28), for the weak TV
    // tiers only (`tier` — catalogue.ts). Ordered by shape: tunnels, kaleidoscopes and mandalas, starbursts, then
    // the calm scenes, the synchrony swarms and the holidays. Free: one per mood, so a free viewer on a weak TV
    // gets a whole set of moods.
    id: 'lite', title: 'Starter', short: 'Starter', badge: 'ST', blurb: 'made to run smooth on any TV',
    worlds: [
      'neon-tunnel', 'op-art-corridor', 'wormhole', 'spiral-stair', 'lantern-tunnel', 'light-tunnel',
      'shard-kaleidoscope', 'prism-kaleidoscope', 'snowflake-kaleidoscope', 'dot-mandala', 'lotus-mandala', 'rangoli-bloom',
      'sunburst', 'comet-shower',
      'aurora-veil', 'wind-meadow', 'moon-pond', 'constellations', 'rain-lake', 'moon-ocean', 'desert-night', 'autumn-pond', 'cherry-blossom', 'snow-village',
      'murmuration', 'drone-formations', 'firefly-sync', 'pendulum-wave',
      'flux-lines', 'wire-lattice', 'glyph-rain', 'zen-garden', 'iso-city',
      'laser-stage', 'eq-skyline', 'confetti-cannons',
      'winter-lights', 'menorah-lights', 'diya-rows', 'midnight-fireworks'
    ],
    free: ['neon-tunnel', 'comet-shower', 'dot-mandala', 'rain-lake', 'moon-pond', 'desert-night'], pro: true, tier: 'lite', feed: 3
  },
  {
    // ── THE FX VOLUMES — the shader-pack library (66 packs, 1,301 visuals), curated 2026-10-26
    // (data/fx-curation: genre, portrait/venue/flash safety, quality, moods). Every visual the curation
    // passes is offered; a visual joins the first volume below whose rule fits its genre. Browse is
    // mood-first on the phones, so volumes are the secondary, by-shape grouping. `feed` leans For You on
    // the Mesmerize-like shapes (tunnels, kaleidoscopes, starbursts, landscapes); the `other` genre
    // (Seasons & Holidays, More) is weighted low. `core` = how many ride inside the app (offline set).
    //
    // TUNNELS — forward fly-throughs and spiral falls.
    id: 'tunnels', title: 'Tunnels', short: 'Tunnels', badge: 'TUN', blurb: 'fly-throughs',
    worlds: fxMembers('tunnels'), fx: { genres: ['tunnel'], core: 28 },
    // free: the three 1.4 picks — all survive the curation cut
    free: ['nature-party/green-tunnel', 'free-chill/soft-fractal', 'free-party/tunnel-rush'], pro: true, feed: 3
  },
  {
    // KALEIDOSCOPES & MANDALAS — mirrored, folding light and centred rings (kept on the 'kaleidoscope'
    // id the 1.4/1.5 builds, saved filters and the Pro "limit to" tokens already use).
    id: 'kaleidoscope', title: 'Kaleidoscopes & Mandalas', short: 'Kaleidoscope', badge: 'KAL', blurb: 'mirrored, folding light',
    worlds: fxMembers('kaleidoscope'), fx: { genres: ['kaleidoscope', 'mandala'], core: 36 },
    // free: the 1.4 picks — slow-kaleido and festival/kaleidoscope (Kaleidoscope), spring/magnolia (a
    // mandala; it was Seasons' pick) — country-club/cut-glass is genre `other` now, so it moved to More
    free: ['free-chill/slow-kaleido', 'festival/kaleidoscope', 'spring/magnolia'], pro: true, feed: 3
  },
  {
    // STARBURSTS — radial blooms, suns, rays.
    id: 'starbursts', title: 'Starbursts', short: 'Starbursts', badge: 'STR', blurb: 'radiant blooms and rays',
    worlds: fxMembers('starbursts'), fx: { genres: ['starburst'], core: 20 },
    // free: a gold sun ringed with snowflakes, art-deco rays, a beaded star (README top picks 24–26)
    free: ['winter-solstice/snow-to-sun', 'new-years-eve/deco-sunburst', 'kwanzaa/muhindi'], pro: true, feed: 2
  },
  {
    // LANDSCAPES — scenes with a horizon: lakes, skies, fields, coasts.
    id: 'landscapes', title: 'Landscapes', short: 'Landscapes', badge: 'LND', blurb: 'skies, water and open land',
    worlds: fxMembers('landscapes'), fx: { genres: ['landscape'], core: 28 },
    // free: an autumn lake (a 1.4 free pick), snow ghosts under the winter sky, alpenglow
    free: ['fall/still-lake', 'winter-solstice/snow-ghosts', 'oktoberfest/alpenglow'], pro: true, feed: 2
  },
  {
    // SEASONS & HOLIDAYS — the remaining (not centre-symmetric, no horizon) visuals of the seasonal and
    // holiday packs: objects, lights, scenes. Kept on the 'seasons' id.
    id: 'seasons', title: 'Seasons & Holidays', short: 'Seasons', badge: 'SEA', blurb: 'winter, spring, summer, fall and the holidays',
    worlds: fxMembers('seasons'), fx: { genres: ['other'], seasonal: true, core: 22 },
    // free: a frozen bubble (a 1.4 free pick), a snowy village, a koi pond
    free: ['winter/frozen-bubble', 'christmas/snowy-village', 'chinese-new-year/koi-pond'], pro: true, feed: 1
  },
  {
    // MORE — everything else the curation passes: patterns, objects, party lights.
    id: 'more', title: 'More Visuals', short: 'More', badge: 'MOR', blurb: 'patterns, objects and party lights',
    worlds: fxMembers('more'), fx: { genres: ['other'], core: 16 },
    // free: cut glass (a 1.4 free pick), city lights after hours, a nautilus
    free: ['country-club/cut-glass', 'office-party/late-lights', 'beach-party/nautilus'], pro: true, feed: 0.5
  }
]

/**
 * THE DEFAULT VISUAL — the first world a new user sees: the player's opening scene, the TV / phone
 * Director's opening world (DirectorOptions.startWorld, wherever the tier's pool holds it), and the
 * fallback for an id that doesn't resolve. Owner decision (1.4.0, after trying Soft Fractal on a real
 * iPhone): Water Ripples, an Ambient & Chill shader world — made a free pick for it. It must stay free
 * (checkVolumes enforces it). An engine shader world needs no package; if the default is ever an fx
 * world again, its package is BUNDLED INLINE (scripts/fx/build-fx-packs.mjs → src/fxDefault.json) so
 * the first frame never waits on a fetch.
 */
export const DEFAULT_WORLD_ID = 'water-ripples'

/** World ids hidden on the TV grid + TV Director journey — non-reactive MVP visuals, dropped on tvOS
 *  too (WorldCatalog.runtimeExcluded). They stay live on web + mobile. */
export const TV_HIDDEN_WORLD_IDS = ['neon-pulse', 'bass-citadel']

/** Flagged-newest worlds — the "NEW" badge on the TV grid + the home-channel highlight, and the native
 *  Apple TV's grid badge + Top Shelf "New worlds" row (catalogue.json `newest`). OTA worlds this build
 *  never shipped with are also treated as new. */
export const TV_NEW_WORLD_IDS = ['inner-voyage', 'cosmic-eye']

/** World modes that are only ever offered through explicit curation in VOLUMES: a pack visual that
 *  no volume names stays out of every lane (Experiments included), whatever its OTA metadata says. */
export const CURATED_ONLY_MODES: readonly string[] = ['fx']

/** The volume that names this world (listed or held), if any. */
export function volumeOfId(id: string): VolumeDef | undefined {
  // VOLUMES is fixed for the life of the page: index it once (≈1,300 ids — every list asks this per world)
  if (!volumeIndex) {
    volumeIndex = new Map()
    for (const v of VOLUMES) for (const w of v.worlds) if (!volumeIndex.has(w)) volumeIndex.set(w, v)
  }
  return volumeIndex.get(id)
}
let volumeIndex: Map<string, VolumeDef> | null = null

/** Is this world gated by a Pro volume definition? undefined = no volume names it. */
export function curatedPro(id: string): boolean | undefined {
  const v = volumeOfId(id)
  return v ? v.pro && !v.free.includes(id) : undefined
}

/** engine 0.14+ flags owner-retired worlds: they still RESOLVE by id (a saved pick, a deep link keep
 *  mounting) but are never offered. Read structurally so this works on both sides of the pin bump. */
export const isRetired = (w: object): boolean => (w as { retired?: unknown }).retired === true

/** The minimal world shape the build-time catalogue needs (a subset of the engine's World). */
export interface LibraryWorld { id: string; name: string; retired?: boolean }

/** One entry of `dist/catalogue.json` — what the native phone pickers render. Everything past
 *  `pro` comes from src/worldFacts.json (scripts/catalogue/build-world-facts.mjs):
 *  - `moods` (1–2, best fit first), `tags` (search words), `fxModes` (an fx visual's mapping modes,
 *    native first — ['follow','drive'] = a 7-seat Follow visual you can switch to Drive);
 *  - the SAFETY flags the settings read: `venueSafe` (venue mode plays only these), `flashSafe` (the
 *    calmest flash tier — "Calm flashes" plays only these), `portraitSafe` (reads in a 9:16 crop),
 *    `slow` (too heavy for the weak TV tiers — hidden there). fx visuals carry the content curation's
 *    verdicts; engine worlds follow their energy (calm = safe), see build-world-facts;
 *  - `genre` (fx only: tunnel / kaleidoscope / mandala / starburst / landscape / other);
 *  - `delivery`: 'core' = in the app bundle (works offline); 'remote' = an EXTENDED fx visual, whose
 *    package and stills load from `fxBase` (top level) — offer it only while that base is reachable,
 *    offline it can't render. Every world is in `worlds` either way (the Pro sheet counts them). */
export interface CatalogueWorld {
  id: string; name: string; volume: string; badge: string; pro: boolean
  /** 'lite' = a tier world (VolumeDef.tier): a native shell lists it only while the TV it casts to offers it
   *  (the offer frame's `extra`) — never in its own feed or pickers otherwise. Absent = every surface. */
  tier?: 'lite'
  moods: string[]; tags: string[]; fxModes: string[]
  venueSafe: boolean; flashSafe: boolean; portraitSafe: boolean; slow: boolean; genre?: string; delivery: 'core' | 'remote'
  /** The world's stills. A RELATIVE path is in the app bundle (from the web root: the native shells serve
   *  or read it from their copy of dist/); an ABSOLUTE url is on the fx base (online only). Any may be
   *  absent. `landscape` 16:9 (TV grids, web gallery); `portraitThumb` 9:16 360×640 (the phone feed's
   *  swipe placeholder, Browse covers — show it at once); `portrait` 9:16 1080×1920 (sharpen to it). */
  previews: { landscape?: string; portraitThumb?: string; portrait?: string }
}
/** Where one still lives: `path` from the web root; `remote` = on the fx base, not in the bundle. */
export interface PreviewRef { path: string; remote: boolean }
/** The generated per-world facts (worldFacts.json) — everything a catalogue entry carries beyond VOLUMES. */
export interface WorldFacts {
  moods: string[]; tags: string[]; fxModes: string[]
  venueSafe: boolean; flashSafe: boolean; portraitSafe: boolean; slow: boolean; genre?: string; remote: boolean
  previews: { landscape?: PreviewRef; portraitThumb?: PreviewRef; portrait?: PreviewRef }
}
/** What the build hands buildCatalogue: moods.ts MOODS, worldFacts.json, and the extended fx base. */
export interface CatalogueFacts {
  defs: readonly { id: string; title: string; blurb: string }[]
  worlds: Readonly<Record<string, WorldFacts>>
  /** Where EXTENDED fx visuals load from (absolute; ends in '/'): `<fxBase><pack>/<slug>.json`, previews
   *  at `<fxBase>previews/<pack>/<slug>.jpg`. */
  fxBase: string
}
export interface CatalogueJson {
  schema: 1
  /** MOODS (moods.ts), in display order — Browse's default grouping on the phones. */
  moods: { id: string; title: string; blurb: string }[]
  /** `blurb` feeds the native TV's locked-world panel ("Part of Volume 2 — the photoreal worlds");
   *  `feed` is the phone feed's For You weight (VolumeDef.feed, resolved — always present). */
  volumes: { id: string; title: string; short: string; badge: string; blurb: string; pro: boolean; feed: number; tier?: 'lite' }[]
  worlds: CatalogueWorld[]
  /** DEFAULT_WORLD_ID — the native TV Director opens on it (DirectorOptions.startWorld). */
  defaultWorld: string
  /** TV_NEW_WORLD_IDS that resolve here — the native TV's NEW badges + Top Shelf "New worlds" row. */
  newest: string[]
  /** The extended fx base (CatalogueFacts.fxBase) — `remote` worlds' packages and previews live here. */
  fxBase: string
}

/**
 * The catalogue as the BUILD sees it — VOLUMES resolved against the library the build bundles (the
 * pinned engine's WORLDS + every fx descriptor, core and extended), with the TV grid's rules (offered
 * volumes in order, retired + TV-hidden worlds dropped). Emitted as dist/catalogue.json by
 * vite.config.ts; the iOS and Android phone shells bundle the web build and read it, and the native
 * Apple TV app bundles it too (apps/tvos/scripts/build-ota-seed.mjs), so their pickers can't drift from
 * the web's. It has no OTA view: a world that only arrives over the air shows in the web pickers, not
 * (yet) in the native ones. A world with no facts entry is a stale worldFacts.json — the build fails
 * on it (vite.config), so the defaults below never ship.
 */
export function buildCatalogue(library: readonly LibraryWorld[], facts: CatalogueFacts): CatalogueJson {
  const hidden = new Set(TV_HIDDEN_WORLD_IDS)
  const byId = new Map(library.map((w) => [w.id, w]))
  const resolved = (v: VolumeDef): LibraryWorld[] =>
    v.worlds.map((id) => byId.get(id)).filter((w): w is LibraryWorld => w !== undefined && !isRetired(w))
  const offered = VOLUMES.filter((v) => v.listed !== false && resolved(v).length > 0)
  const none: WorldFacts = { moods: [], tags: [], fxModes: [], venueSafe: false, flashSafe: false, portraitSafe: false, slow: false, remote: false, previews: {} }
  const url = (r: PreviewRef | undefined): string | undefined => (r ? (r.remote ? `${facts.fxBase}${r.path}` : r.path) : undefined)
  const withPreviews = ({ remote, ...f }: WorldFacts): Omit<CatalogueWorld, 'id' | 'name' | 'volume' | 'badge' | 'pro'> => ({
    ...f,
    delivery: remote ? 'remote' : 'core',
    previews: Object.fromEntries((['landscape', 'portraitThumb', 'portrait'] as const).flatMap((k) => (f.previews[k] ? [[k, url(f.previews[k])]] : [])))
  })
  return {
    schema: 1,
    moods: facts.defs.map((m) => ({ id: m.id, title: m.title, blurb: m.blurb })),
    volumes: offered.map((v) => ({ id: v.id, title: v.title, short: v.short, badge: v.badge, blurb: v.blurb, pro: v.pro, feed: v.feed ?? 1, ...(v.tier ? { tier: v.tier } : {}) })),
    worlds: offered.flatMap((v) => resolved(v).filter((w) => !hidden.has(w.id))
      .map((w) => ({ id: w.id, name: w.name, volume: v.id, badge: v.badge, pro: v.pro && !v.free.includes(w.id), ...(v.tier ? { tier: v.tier } : {}), ...withPreviews(facts.worlds[w.id] ?? none) }))),
    defaultWorld: DEFAULT_WORLD_ID,
    newest: TV_NEW_WORLD_IDS.filter((id) => offered.some((v) => resolved(v).some((w) => w.id === id))),
    fxBase: facts.fxBase
  }
}

/** Invariants of the data above — thrown at build time (vite.config) so a bad edit can't ship. */
export function checkVolumes(): string[] {
  const errors: string[] = []
  const seen = new Map<string, string>()
  const keys = new Set<string>()
  for (const v of VOLUMES) {
    if (keys.has(v.id)) errors.push(`duplicate volume id ${v.id}`)
    keys.add(v.id)
    for (const id of v.worlds) {
      const prev = seen.get(id)
      if (prev) errors.push(`${id} is in both ${prev} and ${v.id}`)
      seen.set(id, v.id)
    }
    for (const id of v.free) if (!v.worlds.includes(id)) errors.push(`${v.id}: free world ${id} is not in the volume`)
    if (!v.pro && v.free.length) errors.push(`${v.id}: free list on a non-Pro volume`)
    if (v.feed !== undefined && !(Number.isFinite(v.feed) && v.feed >= 0)) errors.push(`${v.id}: feed weight must be a number ≥ 0`)
  }
  if (keys.has('all') || keys.has('exp')) errors.push('volume ids "all" / "exp" are reserved filter values')
  for (const v of VOLUMES) if (v.tier && v.fx) errors.push(`${v.id}: a tier volume is a hand list, not an fx rule`)
  const home = volumeOfId(DEFAULT_WORLD_ID)
  if (!home || home.listed === false || home.tier) errors.push(`default world ${DEFAULT_WORLD_ID} is in no listed, untiered volume`)
  else if (home.pro && !home.free.includes(DEFAULT_WORLD_ID)) errors.push(`default world ${DEFAULT_WORLD_ID} is Pro-gated — the first visual must be free`)
  return errors
}
